import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { PackageIcon, PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import type { CarrierDTO, CarrierServiceDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { carrierServiceCategoryTitle } from '@/shared/title-maps';

const columnHelper = createColumnHelper<CarrierServiceDTO>();

function getColumns(carriersById: Record<string, CarrierDTO>) {
  return [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('code', { header: 'Code' }),
    columnHelper.accessor('carrierId', {
      header: 'Carrier',
      cell: ({ getValue }) => carriersById?.[getValue()]?.name ?? '',
    }),
    columnHelper.accessor('category', {
      header: 'Category',
      cell: ({ getValue }) => carrierServiceCategoryTitle[getValue()],
    }),
    columnHelper.accessor('externalCodes', {
      header: 'External codes',
      cell: ({ getValue }) => getValue().join(', '),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created',
      cell: ({ getValue }) => formatDateTime(getValue()),
    }),
  ];
}

export function CarrierServiceList() {
  const { shippingGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<CarrierServiceDTO>('/shipping/carrier_services');

  const { data: carriersData } = useQuery({
    queryKey: ['carriers'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarriers({ cursor, take }, { signal }),
      ),
  });

  const carriersById = useMemo(
    () => carriersData?.reduce((acc, carrier) => ({ ...acc, [carrier.id]: carrier }), {}) ?? {},
    [carriersData],
  );

  const columns = useMemo(() => getColumns(carriersById), [carriersById]);

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({ columns });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'carrierServices',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierServices({ q: input.search, cursor, take }, { signal }),
      ),
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <PackageIcon />
        </HeaderIcon>
        <HeaderTitle>Carrier services</HeaderTitle>
        <HeaderActions>
          <Button asChild>
            <Link to="/shipping/carrier_services/new">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
